import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Box, IconButton, Typography, TablePagination } from '@mui/material';

import { buildQueryParams } from 'src/components/common/table/dataTableQueryBuilder';
import { SearchInput } from 'src/components/common/table/SearchInput';
import { ITableQueryState } from 'src/components/common/types/tableTypes';
import EmptySiteCard from 'src/features/site/components/EmptySiteCard';
import {
	IPagedSitesContainerProps,
	ISiteDetails,
	IGetSitesResponse,
} from 'src/features/site/domain/types';
import { getSites, SITE_LOADING_KEY } from 'src/features/site/store';
import WithRoleAccess from 'src/routes/WithRoleAccess';
import { AppDispatch, RootState } from 'src/store';
import { formatTimestamp } from 'src/utils/dateUtils';

import SiteCard from './SiteCard';

const DATA_TABLE_ROWS_PER_PAGE =
	Number(process.env.REACT_APP_DATA_TABLE_ROWS_PER_PAGE) || 10;

const PagedSitesContainer: React.FC<IPagedSitesContainerProps> = ({
	refreshSites,
	onCreateSiteClick,
}) => {
	const isLoading = useSelector(
		(state: RootState) => state.loading.loadingStates[SITE_LOADING_KEY],
	);
	const isInitialLoad = useRef(true);
	const [queryState, setQueryState] = useState<ITableQueryState>({ page: 0 });
	const [sites, setSites] = useState<ISiteDetails[]>([]);
	const [sitesPageInfo, setSitesPageInfo] =
		useState<IGetSitesResponse['info']>();
	const dispatch = useDispatch<AppDispatch>();

	const handleChangePage = (event: unknown, newPage: number) => {
		setQueryState({ ...queryState, page: newPage });
	};

	useEffect(() => {
		if (refreshSites.refreshSites) {
			setQueryState({ page: 0 });
		}
	}, [refreshSites]);

	useEffect(() => {
		const params = buildQueryParams(queryState);
		dispatch(
			getSites({
				request: params,
				onSuccess: (response) => {
					if (isInitialLoad.current) {
						isInitialLoad.current = false;
					}
					setSites(response.results);
					setSitesPageInfo(response.info);
				},
			}),
		);
	}, [queryState]);

	if (
		!isLoading &&
		!isInitialLoad.current &&
		sites.length === 0 &&
		!queryState.searchText
	) {
		return <EmptySiteCard onCreateSite={onCreateSiteClick} />;
	}

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 3,
				padding: 3,
				backgroundColor: 'white',
				borderRadius: '4px',
				marginTop: '20px',
			}}
		>
			{/* Sites title */}
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<Typography
					variant='h6'
					sx={{
						fontSize: '20px',
						fontWeight: 500,
						color: 'rgba(0, 0, 0, 0.87)',
					}}
				>
					Sites
				</Typography>
				<WithRoleAccess allowedGlobalRoles={['super_admin']}>
					<IconButton
						onClick={onCreateSiteClick}
						sx={{
							padding: 0,
							'&:hover': {
								backgroundColor: 'transparent',
							},
						}}
					>
						<AddCircleOutlineIcon
							sx={{
								fontSize: '20px',
								color: 'rgba(0, 0, 0, 0.54)',
							}}
						/>
					</IconButton>
				</WithRoleAccess>
			</Box>

			<SearchInput
				initialValue={queryState.searchText || ''}
				onSearch={(text: string) =>
					setQueryState({
						...queryState,
						searchText: text,
						page: 0,
					})
				}
				onClear={() =>
					setQueryState({
						...queryState,
						searchText: '',
						page: 0,
					})
				}
			/>

			{/* Sites grid */}
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: {
						xs: '1fr',
						sm: 'repeat(2, 1fr)',
						md: 'repeat(3, 1fr)',
						lg: 'repeat(4, 1fr)',
						xl: 'repeat(5, 1fr)',
					},
					gap: 2,
				}}
			>
				{sites.map((site) => (
					<SiteCard
						key={site.id}
						siteName={site.name}
						patientCount={site.total_patients || 0}
						lastUpdated={formatTimestamp(site.created_at)}
						siteSlug={site.slug}
					/>
				))}
			</Box>

			{/* No results message */}
			{sites.length === 0 && queryState.searchText && (
				<Typography
					variant='body1'
					color='text.secondary'
					align='center'
				>
					{`No sites found matching "${queryState.searchText}"`}
				</Typography>
			)}

			{/* Pagination controls */}
			{sitesPageInfo && (
				<TablePagination
					component='div'
					count={sitesPageInfo.count}
					page={queryState.page || 0}
					onPageChange={handleChangePage}
					rowsPerPage={DATA_TABLE_ROWS_PER_PAGE}
					rowsPerPageOptions={[DATA_TABLE_ROWS_PER_PAGE]}
				/>
			)}
		</Box>
	);
};

export default PagedSitesContainer;
